/**
 * HistoryChart — the per-triage queue-length history as a multi-line chart.
 *
 * Pure and presentational: it takes the already-transformed {@link ChartSeries}
 * plus explicit pixel dimensions and draws SVG. The responsive sizing lives in
 * the parent (HistoryChartCard wraps it in `<ParentSize>`), which keeps this
 * component deterministic — unit tests render it at a fixed size, no
 * ResizeObserver needed.
 *
 * Built on visx (the locked chart library, see DECISIONS.md): `scaleTime` /
 * `scaleLinear` for the axes, `LinePath` for each triage line with a `defined`
 * accessor so collector-downtime gaps break the line instead of drawing a
 * misleading drop to zero. Lines and the spread band use `curveMonotoneX` —
 * smooth like a health app, but monotone so the curve never overshoots a data
 * point (no invented peaks/dips; honesty first, see DESIGN.md).
 *
 * Legend mute/solo: the card owns a `hidden` set of triage codes; muted series
 * ease out via a CSS opacity transition (`data-muted`) and drop out of the
 * y-scale, which rescales to the visible peak while staying anchored at 0.
 *
 * Colour is not the only channel: each line is also labelled in the legend the
 * card renders, and the full series is exposed as an sr-only data table there,
 * so the trend is legible without seeing the hues (WCAG 2.1 AA).
 */

import { useId, type CSSProperties } from "react";
import { scaleTime, scaleLinear } from "@visx/scale";
import { Area, LinePath } from "@visx/shape";
import { curveMonotoneX } from "@visx/curve";
import { Group } from "@visx/group";
import { AxisBottom, AxisLeft } from "@visx/axis";
import { GridRows } from "@visx/grid";
import {
  isBandDrawable,
  isDrawable,
  timeDomain,
  valueMax,
  type ChartPoint,
  type ChartSeries,
} from "@/lib/history";
import { formatRomeClock } from "@/lib/time";

interface HistoryChartProps {
  series: ChartSeries[];
  /** Outer width in px; the chart renders nothing at non-positive widths. */
  width: number;
  /** Outer height in px. */
  height: number;
  /** Accessible summary for the chart as a whole. */
  ariaLabel: string;
  /**
   * Formats an x-axis tick instant. Defaults to the Europe/Rome wall clock
   * (HH:mm) for the intraday history; the long-window trend passes a day/month
   * formatter so a multi-week axis reads as dates, not repeated "00:00"s.
   */
  xTickFormat?: (date: Date) => string;
  /**
   * Triage codes the user has muted via the legend. Muted series ease out
   * (opacity, via CSS) and drop out of the y-scale so it rescales to the visible
   * peak — still anchored at 0 (honest). Defaults to "nothing muted".
   */
  hidden?: ReadonlySet<number>;
}

const MARGIN = { top: 12, right: 16, bottom: 28, left: 32 } as const;

export function HistoryChart({
  series,
  width,
  height,
  ariaLabel,
  xTickFormat = formatRomeClock,
  hidden,
}: HistoryChartProps) {
  const clipId = useId();
  const innerWidth = width - MARGIN.left - MARGIN.right;
  const innerHeight = height - MARGIN.top - MARGIN.bottom;
  const domain = timeDomain(series);

  // Nothing to size against yet (initial measure) or no plottable window.
  if (innerWidth <= 0 || innerHeight <= 0 || domain === null) return null;

  const xScale = scaleTime({ domain, range: [0, innerWidth] });
  const yScale = scaleLinear({
    // Rescale to the *visible* peak so soloing a small line lets it breathe;
    // still anchored at 0, so a muted-out series never distorts the axis.
    domain: [0, valueMax(series, hidden)],
    range: [innerHeight, 0],
    nice: true,
  });

  const xTickCount = Math.max(2, Math.min(6, Math.floor(innerWidth / 90)));
  // Cap the y-tick count to the domain's integer span: the labels are rounded to
  // whole people (`Math.round`), so on a small visible max (e.g. a soloed 0–2
  // line) an unclamped count yields fractional ticks that collapse to duplicate
  // integers ("2, 2, 1, 1, 0"). Never request more ticks than there are integers.
  const yDomainMax = yScale.domain()[1] ?? 1;
  const yTickCount = Math.max(2, Math.min(5, Math.floor(innerHeight / 36), Math.ceil(yDomainMax)));

  return (
    <svg className="history-chart" width={width} height={height} role="img" aria-label={ariaLabel}>
      <Group left={MARGIN.left} top={MARGIN.top}>
        <GridRows
          scale={yScale}
          width={innerWidth}
          numTicks={yTickCount}
          className="history-chart__grid"
        />
        <AxisLeft
          scale={yScale}
          numTicks={yTickCount}
          hideAxisLine
          tickLength={4}
          tickFormat={(v) => `${Math.round(v.valueOf())}`}
          tickClassName="history-chart__tick"
        />
        <AxisBottom
          scale={xScale}
          top={innerHeight}
          numTicks={xTickCount}
          hideAxisLine
          tickLength={4}
          tickFormat={(v) => xTickFormat(new Date(v.valueOf()))}
          tickClassName="history-chart__tick"
        />
        {/* Clip the drawn marks to the plot rect: when the y-scale rescales to a
            smaller visible peak (a solo/mute), a muted line still easing out
            would otherwise overshoot the top edge — the clip keeps the fade
            clean instead of letting a ghost shoot over the axis. */}
        <defs>
          <clipPath id={clipId}>
            <rect x={0} y={0} width={innerWidth} height={innerHeight} />
          </clipPath>
        </defs>
        <g clipPath={`url(#${clipId})`}>
          {/* Spread band (trend only): the min/max range as a translucent fill,
              drawn under every line so the averages read on top. History series
              carry no band, so this renders nothing for them. */}
          {series.map((s) =>
            s.points.some(isBandDrawable) ? (
              <Area<ChartPoint>
                key={`band-${s.triageCode}`}
                className="history-chart__band history-chart__mark"
                data-muted={hidden?.has(s.triageCode) ? "1" : undefined}
                // The band fill + per-theme opacity live in CSS (see index.css): a dark
                // surface needs the cone lifted to bloom, not the same value inverted.
                // The series colour is handed to the stylesheet via a custom property.
                style={{ ["--series"]: s.color } as CSSProperties}
                curve={curveMonotoneX}
                data={s.points}
                defined={isBandDrawable}
                x={(p) => xScale(p.t)}
                y0={(p) => yScale(p.lo ?? 0)}
                y1={(p) => yScale(p.hi ?? 0)}
                stroke="none"
              />
            ) : null,
          )}
          {series.map((s) => (
            <LinePath
              key={s.triageCode}
              className="history-chart__mark"
              data-muted={hidden?.has(s.triageCode) ? "1" : undefined}
              curve={curveMonotoneX}
              data={s.points}
              defined={isDrawable}
              x={(p) => xScale(p.t)}
              y={(p) => yScale(p.value ?? 0)}
              stroke={s.color}
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
              shapeRendering="geometricPrecision"
            />
          ))}
        </g>
      </Group>
    </svg>
  );
}
